import {
  areFieldsDirty,
  buildAllFieldsFromSections,
  buildFormValuesFromFields,
  buildPayloadFromFields,
  markSeededDefaultsDirty,
  Card,
  CardContent,
  Form,
  FormBuilderSections,
  getResolver,
  QuoteDerogationLockNotice,
  resolveSectionsFieldShortcuts,
  toast,
  useStepperContext,
} from '@folio/components';
import { FormBuilderFooter } from '@folio/components/forms/form-builder/FormBuilderFooter';
import {
  Asset,
  BranchDto,
  BrokerageFeeOnPolicy,
  clientWithFork,
  configControllerGetPublicProductConfigQueryOptions,
  ConfigQuoteChosenWarrantyDto,
  Customer,
  customerControllerGetCustomerQueryOptions,
  CustomFieldDto,
  Duration,
  EditPolicyInputBody,
  GetBrokerageFirmOutputFirmTypeEnumKey,
  Insurer,
  getNewBranchIdAfterFork,
  keyValueFieldsToObject,
  Policy,
  QuoteConfig,
  useAssetControllerEditAsset,
  usePolicyControllerEditPolicy,
  usePolicyControllerQuotePolicy,
  usePolicyControllerSetPolicyFees,
  usePolicyControllerUpdatePolicyQuote,
} from 'korint-sdk';
import { FormSections, FormSectionsWithShortcuts } from 'korint-sdk/react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { QuoteRecapModule } from '../QuoteRecapModule/QuoteRecapModule';
import { QuoteTierSelectModule } from '../QuoteTierSelectModule/QuoteTierSelectModule';
import { QuoteOptionsSelectModule } from '../QuoteOptionsSelectModule/QuoteOptionsSelectModule';
import { useCalculateQuotes } from '../shared-quote/use-calculate-quotes';
import {
  getAdditionalQuoteComponentsFromFormPayload,
  getAdditionalQuotePercentagesFromFormPayload,
  earlierBound,
  laterBound,
  resolveAssetStartedAtBounds,
} from '../shared-quote/utils';
import { QuoteContext } from '../shared-quote/QuoteContext';
import { ZeroPremiumErrorBanner } from '../PolicyWarningBanner/ZeroPremiumErrorBanner';
import { skipsStartedAtRelativeMinimum } from '@folio/utils/skipStartedAtRelativeMinimum';
import { useHasPolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useQuery } from '@tanstack/react-query';
import { Stack } from '@folio/components/ui/flex';

type DelegatedQuoteModuleProps = {
  mode?: 'stepper' | 'standalone';
  showPreviousButton?: boolean;
  showQuoteRecap?: 'aside' | 'inline' | false;
  firmType: GetBrokerageFirmOutputFirmTypeEnumKey | undefined;
  policy: Policy;
  branch: BranchDto;
  asset: Asset;
  assets: Asset[];
  quoteConfig: QuoteConfig;
  policySections?: FormSectionsWithShortcuts;
  policySectionsAfterOptions?: FormSectionsWithShortcuts;
  assetSections?: FormSectionsWithShortcuts;
  additionalQuoteComponentsSections?: Array<FormSections[number] & { forTypes?: string[] }>;
  additionalComponent?: React.ReactNode;
  additionalComponentAfterOptions?: React.ReactNode;
  overrideDelegatedComponents?: React.ReactNode;
  helper?: React.ReactNode;
  disabled?: boolean;
};

export function DelegatedQuoteSections({
  mode = 'stepper',
  showPreviousButton = true,
  showQuoteRecap = 'aside',
  firmType,
  policy,
  branch,
  asset,
  assets,
  quoteConfig,
  policySections: rawPolicySections = [],
  policySectionsAfterOptions: rawPolicySectionsAfterOptions = [],
  assetSections: rawAssetSections = [],
  additionalQuoteComponentsSections: rawAdditionalQuoteComponentsSections = [],
  additionalComponent,
  additionalComponentAfterOptions,
  overrideDelegatedComponents,
  helper,
  disabled,
}: DelegatedQuoteModuleProps) {
  const stepper = useStepperContext();

  const assetIds = assets?.map(asset => asset.id) ?? [];

  const {
    data: {
      policyFields: policyFieldsConfig,
      assetFields: assetFieldsConfig,
      assetStartedAtLimit,
      insurers,
    } = {},
  } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
  });

  const newBusinessInsurers = branch.branchType === 'NEW_BUSINESS' ? insurers : undefined;

  const policySections: FormSections = useMemo(
    () =>
      resolveSectionsFieldShortcuts(rawPolicySections, policyFieldsConfig ?? [])
        .filter(section => !(section.forTypes && !section.forTypes.includes(firmType ?? '')))
        .map(section => {
          const fields = Array.from(section.fields);

          const hasBrokerageFirmField = section.fields.some(
            field => field.name === 'brokerageFirm',
          );

          if (!hasBrokerageFirmField) {
            fields.push({
              name: 'brokerageFirm',
              type: 'text',
              hidden: true,
              ignoreInPayload: true,
              required: false,
            } as const);
          }

          const hasInvoicingFrequencyField = section.fields.some(
            field => field.name === 'invoicingConfig.frequency',
          );

          if (!hasInvoicingFrequencyField) {
            fields.push({
              name: 'invoicingConfig.frequency',
              type: 'text',
              hidden: true,
              ignoreInPayload: true,
              required: false,
              apiDataAccessor: 'invoicingConfig.frequency',
            } as const);
          }

          return {
            ...section,
            fields,
          };
        })
        .filter(section => section.fields.some(f => !f.hidden)),
    [rawPolicySections, policyFieldsConfig, firmType],
  );

  const policySectionsAfterOptions: FormSections = useMemo(
    () =>
      resolveSectionsFieldShortcuts(rawPolicySectionsAfterOptions, policyFieldsConfig ?? [])
        .filter(section => !(section.forTypes && !section.forTypes.includes(firmType ?? '')))
        .filter(section => section.fields.some(f => !f.hidden)),
    [rawPolicySectionsAfterOptions, policyFieldsConfig, firmType],
  );

  const skipRelativeMinimum = skipsStartedAtRelativeMinimum();

  const assetSections: FormSections = useMemo(() => {
    const startedAtBounds = resolveAssetStartedAtBounds(assetStartedAtLimit, {
      ignoreRelativeMinimum: skipRelativeMinimum,
    });

    return resolveSectionsFieldShortcuts(rawAssetSections, assetFieldsConfig ?? [])
      .map(section => ({
        ...section,
        /**
         * WARNING disallow editing startedAt if asset was created not on the current branch, to avoid being blocked in MTA
         */
        fields: section.fields
          .filter(({ name }) => name !== 'startedAt' || asset?.createdOnBranch)
          .map(field =>
            field.name === 'startedAt' && (field.type === 'datetime' || field.type === 'date')
              ? {
                  ...field,
                  min: laterBound(startedAtBounds.min, field.min),
                  max: earlierBound(startedAtBounds.max, field.max),
                }
              : field,
          ),
      }))
      .filter(section => section.fields.some(f => !f.hidden));
  }, [
    rawAssetSections,
    asset?.createdOnBranch,
    assetFieldsConfig,
    assetStartedAtLimit,
    skipRelativeMinimum,
  ]);

  const additionalQuoteComponentsSections: FormSections = useMemo(
    () =>
      rawAdditionalQuoteComponentsSections
        .map(section => ({
          ...section,
          fields: section.fields.filter(f => !f.hidden),
        }))
        .filter(
          section =>
            section.fields.some(f => !f.hidden) &&
            !(section.forTypes && !section.forTypes.includes(firmType ?? '')),
        ),
    [JSON.stringify(rawAdditionalQuoteComponentsSections), firmType],
  );

  const policyFields = useMemo(
    () => buildAllFieldsFromSections([...policySections, ...policySectionsAfterOptions]),
    [policySections, policySectionsAfterOptions],
  );
  const assetFields = useMemo(() => buildAllFieldsFromSections(assetSections), [assetSections]);
  const additionalQuoteComponentsFields = useMemo(
    () => buildAllFieldsFromSections(additionalQuoteComponentsSections),
    [additionalQuoteComponentsSections],
  );

  const form = useForm({
    mode: 'all',
    values: {
      insurer: newBusinessInsurers
        ? (policy.insurer ?? quoteConfig?.defaultConfig.insurer)
        : undefined,
      tier: asset?.quote?.options?.tier ?? quoteConfig?.defaultConfig.tier,
      excess: asset?.quote?.options?.excess ?? quoteConfig?.defaultConfig.excess,
      ...buildFormValuesFromFields(policyFields, policy),
      ...buildFormValuesFromFields(assetFields, asset),
      ...buildFormValuesFromFields(additionalQuoteComponentsFields, policy),
    } as Record<string, unknown>,
    resolver: getResolver([...policyFields, ...assetFields, ...additionalQuoteComponentsFields]),
  });

  const formValues = form.watch();

  const { data: customer } = useQuery({
    ...customerControllerGetCustomerQueryOptions(policy.customers?.[0] ?? '', {
      branchId: branch.id,
    }),
    select: data =>
      ({ ...data, customFields: keyValueFieldsToObject(data.customFields ?? []) }) as Customer,
    enabled: Boolean(policy.customers?.[0]),
  });

  const perilValuesKey = useMemo(
    () =>
      JSON.stringify(
        Object.entries(formValues)
          .filter(([key]) => key.startsWith('peril-'))
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, value]) => [key, value]),
      ),
    [formValues],
  );

  const chosenPerils = useMemo(
    () =>
      Object.entries(formValues)
        .filter(([key, value]) => key.startsWith('peril-') && value !== 'false')
        .map(([key, value]) =>
          value === 'true'
            ? {
                id: key.replace('peril-', ''),
              }
            : {
                id: key.replace('peril-', ''),
                option: value as string,
              },
        )
        .sort((a, b) => a.id.localeCompare(b.id)),
    [perilValuesKey],
  );

  const formValuesKey = useMemo(
    () =>
      JSON.stringify(
        Object.entries(formValues)
          .filter(([key]) => {
            const allFields = [...policyFields, ...assetFields, ...additionalQuoteComponentsFields];
            const fieldNames = new Set(allFields.map(f => f.name));
            return fieldNames.has(key) || key.startsWith('peril-');
          })
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, value]) => [key, value]),
      ),
    [policyFields, assetFields, additionalQuoteComponentsFields, formValues],
  );

  const payload = useMemo(
    () =>
      buildPayloadFromFields(
        [...policyFields, ...assetFields, ...additionalQuoteComponentsFields],
        formValues,
      ) as {
        fees?: BrokerageFeeOnPolicy[];
        periodDuration?: Duration;
        customFields?: CustomFieldDto[];
      },
    [formValuesKey],
  );

  const fees = useMemo(() => payload.fees, [formValuesKey]);

  const additionalQuoteComponents = useMemo(
    () => getAdditionalQuoteComponentsFromFormPayload(payload),
    [formValuesKey],
  );

  const additionalQuotePercentages = useMemo(
    () => getAdditionalQuotePercentagesFromFormPayload(payload),
    [formValuesKey],
  );

  const { data: calculatedQuotes, isLoading: isCalculatingQuotesLoading } = useCalculateQuotes({
    productId: policy.productId,
    policyId: policy.id,
    branchId: branch.id,
    assetIds,
    chosenPerils,
    brokerageFeesOverride: fees,
    periodDurationOverride: payload.periodDuration,
    customFieldsToOverride: payload.customFields,
    additionalQuoteComponents,
    additionalQuotePercentages,
    prorated: true,
    insurers: newBusinessInsurers,
  });

  const blockOnZeroPremium = useHasPolicyProductConfig('blockOnZeroPremium');
  const pricedQuotes = calculatedQuotes.filter(({ quote, error }) => quote && !error);
  const hasZeroPremium =
    blockOnZeroPremium &&
    !isCalculatingQuotesLoading &&
    pricedQuotes.length > 0 &&
    pricedQuotes.every(({ breakdown }) => (breakdown?.recurring?.withFeesAndTaxes ?? 0) === 0);

  useEffect(() => {
    const existingOptions = asset?.quote?.options;

    const defaultConfig = quoteConfig?.defaultConfig;

    const defaultOptions = existingOptions ?? defaultConfig;

    if (!defaultOptions) return;

    form.setValue('tier', defaultOptions.tier, { shouldDirty: !Boolean(existingOptions?.excess) });
    form.setValue('excess', defaultOptions.excess, {
      shouldDirty: !Boolean(existingOptions?.excess),
    });

    for (const { id } of quoteConfig?.availablePerils || []) {
      const defaultPeril = defaultOptions.perils?.find(p => p.id === id);
      const existingPeril = existingOptions?.perils?.find(p => p.id === id);

      const peril = existingPeril ?? defaultPeril;
      const value = peril ? (peril.option ?? 'true') : 'false';

      form.setValue(`peril-${id}`, value, { shouldDirty: !Boolean(existingPeril) });
    }
  }, [quoteConfig, asset?.quote?.options]);

  useEffect(() => {
    const tier = formValues.tier as string | undefined;

    const tierConfig = quoteConfig?.availableConfigs?.find(config => config.tier === tier);
    const defaultConfig = quoteConfig?.defaultConfig;

    if (!tierConfig) return;

    const existingOptions = asset?.quote?.options;
    const existingPerils = existingOptions?.perils ?? [];

    for (const { id } of quoteConfig?.availablePerils || []) {
      const peril = tierConfig.perils?.find(config => config.id === id);
      const existingPeril = existingPerils.find(p => p.id === id);
      const currentValue = formValues[`peril-${id}`] ?? 'false';

      if (existingPeril) {
        if (!peril) {
          form.setValue(`peril-${id}`, 'false');
          continue;
        }

        const preservedValue = existingPeril.option ?? 'true';
        if (currentValue !== preservedValue) {
          form.setValue(`peril-${id}`, preservedValue);
        }
        continue;
      }

      if (peril) {
        const { options = [] } = peril;
        const defaultConfigPeril = defaultConfig?.perils?.find(p => p.id === id);
        const defaultValue = extractDefaultPerilValue(defaultConfigPeril, options, peril.required);

        // If peril is required and currently 'false', set to default value
        if (peril.required && currentValue === 'false') {
          form.setValue(`peril-${id}`, defaultValue);
        } else if (!peril.required && currentValue !== 'false') {
          // If peril is not required and has an invalid value, set to default value
          const isValidOption = currentValue === 'true' || options.includes(currentValue as string);

          if (!isValidOption) {
            form.setValue(`peril-${id}`, defaultValue);
          }
        }
      } else if (currentValue !== 'false' && currentValue !== undefined) {
        // If peril is not in tierConfig, set to 'false' if it has a value
        form.setValue(`peril-${id}`, 'false');
      }
    }
  }, [formValues.tier, quoteConfig, asset?.quote?.options]);

  /**
   * Mutations
   */
  const { mutateAsync: quotePolicy } = usePolicyControllerQuotePolicy({
    client: { client: clientWithFork },
    mutation: {
      onError: error => {
        const detailedError = error as { errorResponse?: { data?: { code?: string } } };

        if (detailedError?.errorResponse?.data?.code === 'NO_CHANGES_TO_QUOTE') {
          toast({
            variant: 'warning',
            title: 'Prime inchangée',
            description: "Aucun champ tarifant n'a été modifié lors de cet avenant.",
          });

          if (mode === 'stepper') {
            stepper.next();
          }

          return;
        }

        throw error;
      },
    },
  });

  const { mutateAsync: updateAsset } = useAssetControllerEditAsset({
    client: { client: clientWithFork },
  });

  const { mutateAsync: updatePolicy } = usePolicyControllerEditPolicy({
    client: { client: clientWithFork },
  });

  const { mutateAsync: updatePolicyFees } = usePolicyControllerSetPolicyFees({
    client: { client: clientWithFork },
  });

  const { mutateAsync: updateQuotePolicy } = usePolicyControllerUpdatePolicyQuote({
    client: { client: clientWithFork },
  });

  const onSubmit = form.handleSubmit(async data => {
    if (hasZeroPremium) return;

    const { dirtyFields } = form.formState;
    const additionalQuoteComponentsDirtyFields = markSeededDefaultsDirty(
      additionalQuoteComponentsFields,
      policy,
      dirtyFields,
    );

    let mutationBranchId: string = branch?.id!;

    if (policyFields.length > 0 && areFieldsDirty(policyFields, dirtyFields)) {
      const { fees, ...policyBody } = buildPayloadFromFields(
        policyFields,
        data,
        dirtyFields,
      ) as Partial<EditPolicyInputBody> & { fees?: BrokerageFeeOnPolicy[] };

      if (!isEmpty(policyBody)) {
        const response = await updatePolicy({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: policyBody,
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }

      // updatePolicyFees doesn't have the same restrictions as updatePolicy on QUOTED status,
      // and thus must be perform after instead of before to avoid losing updates on fork
      if (fees !== undefined) {
        const response = await updatePolicyFees({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: { fees },
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }
    }

    if (assetFields.length > 0 && areFieldsDirty(assetFields, dirtyFields)) {
      for (const assetId of assetIds) {
        // we don't want to update by mistake assets that are being replaced
        const isBeingReplaced = assets?.some(asset => asset.originAssetId === assetId);
        if (isBeingReplaced) continue;

        const response = await updateAsset({
          assetId,
          params: {
            branchId: mutationBranchId,
            skipStartedAtRelativeMinimum: skipRelativeMinimum || undefined,
          },
          data: buildPayloadFromFields(assetFields, data, dirtyFields),
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }
    }

    // Save policy-level fields (customFields, fees) from additionalQuoteComponentsSections.
    // These are distinct from quote components/percentages and must go through the policy endpoints.
    if (
      additionalQuoteComponentsFields.length > 0 &&
      areFieldsDirty(additionalQuoteComponentsFields, additionalQuoteComponentsDirtyFields)
    ) {
      const { fees: additionalFees, customFields: additionalCustomFields } = buildPayloadFromFields(
        additionalQuoteComponentsFields,
        data,
        additionalQuoteComponentsDirtyFields,
      ) as { customFields?: CustomFieldDto[]; fees?: BrokerageFeeOnPolicy[] };

      if (additionalCustomFields?.length) {
        const response = await updatePolicy({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: { customFields: additionalCustomFields },
        });
        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) mutationBranchId = newBranchId;
      }

      if (additionalFees !== undefined) {
        const response = await updatePolicyFees({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: { fees: additionalFees },
        });
        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) mutationBranchId = newBranchId;
      }
    }

    const additionalQuoteComponentsPayload = buildPayloadFromFields(
      additionalQuoteComponentsFields,
      data,
    );

    const hasDirtyFieldsForQuote =
      form.getFieldState('insurer')?.isDirty ||
      form.getFieldState('tier')?.isDirty ||
      form.getFieldState('excess')?.isDirty ||
      Object.keys(formValues)
        .filter(key => key.startsWith('peril-'))
        .some(key => form.getFieldState(key)?.isDirty);

    const isPolicyStatusNotQuoted = policy.status !== 'POLICY_QUOTED';

    let newBranchId: string | undefined;
    if (isPolicyStatusNotQuoted || hasDirtyFieldsForQuote) {
      const response = await quotePolicy({
        policyId: policy.id,
        params: { branchId: mutationBranchId },
        data: {
          insurer: formValues.insurer as Insurer | undefined,
          options: assetIds.map(assetId => ({
            assetId,
            tier: formValues.tier as string,
            excess: formValues.excess as string,
            perils: chosenPerils,
          })),
          additionalQuoteComponentsAndPercentages: {
            components: getAdditionalQuoteComponentsFromFormPayload(
              additionalQuoteComponentsPayload,
            ),
            percentages: getAdditionalQuotePercentagesFromFormPayload(
              additionalQuoteComponentsPayload,
            ),
          },
        },
      });

      newBranchId = getNewBranchIdAfterFork(response);
    } else if (
      areFieldsDirty(additionalQuoteComponentsFields, additionalQuoteComponentsDirtyFields)
    ) {
      const response = await updateQuotePolicy({
        policyId: policy.id,
        params: { branchId: mutationBranchId },
        data: {
          components: getAdditionalQuoteComponentsFromFormPayload(additionalQuoteComponentsPayload),
          percentages: getAdditionalQuotePercentagesFromFormPayload(
            additionalQuoteComponentsPayload,
          ),
        },
      });

      newBranchId = getNewBranchIdAfterFork(response);
    }

    if (newBranchId) {
      mutationBranchId = newBranchId;
    }

    if (mode === 'stepper') {
      stepper.next(mutationBranchId ? { branchId: mutationBranchId } : undefined);
    }
  });

  const centerSection = (
    <Stack>
      {hasZeroPremium && <ZeroPremiumErrorBanner />}
      {additionalComponent}
      {disabled && <QuoteDerogationLockNotice />}
      {assetSections.length > 0 && (
        <FormBuilderSections sections={assetSections} externalData={asset} />
      )}
      {policySections.length > 0 && (
        <FormBuilderSections
          sections={policySections}
          externalData={{ ...policy, asset, branchId: branch?.id }}
          disabled={disabled}
        />
      )}
      {overrideDelegatedComponents ? (
        overrideDelegatedComponents
      ) : (
        <>
          <QuoteTierSelectModule />
          <QuoteOptionsSelectModule />
        </>
      )}
      {policySectionsAfterOptions.length > 0 && (
        <FormBuilderSections
          sections={policySectionsAfterOptions}
          externalData={{ ...policy, asset, branchId: branch?.id }}
          disabled={disabled}
        />
      )}
      {additionalQuoteComponentsSections.length > 0 && (
        <FormBuilderSections sections={additionalQuoteComponentsSections} externalData={policy} />
      )}
      {additionalComponentAfterOptions}
    </Stack>
  );

  return (
    <QuoteContext.Provider
      value={{
        policy,
        asset,
        customer,
        quoteConfig,
        calculatedQuotes,
        isLoading: isCalculatingQuotesLoading,
      }}
    >
      <Form {...form}>
        <form className="flex flex-col gap-4" onSubmit={e => e.preventDefault()}>
          {showQuoteRecap === 'aside' ? (
            <Stack className="xl:flex-row">
              <div className="w-full xl:w-5/7">{centerSection}</div>
              <Stack className="w-full xl:w-2/7 sticky top-4 self-start">
                <QuoteRecapModule />
                {helper && (
                  <Card>
                    <CardContent className="pt-6">{helper}</CardContent>
                  </Card>
                )}
              </Stack>
            </Stack>
          ) : showQuoteRecap === 'inline' ? (
            <>
              {centerSection}
              <QuoteRecapModule />
            </>
          ) : (
            centerSection
          )}
          <FormBuilderFooter
            mode={mode}
            showPreviousButton={showPreviousButton}
            form={form}
            onSubmit={onSubmit}
            nextButtonEnabled={!hasZeroPremium}
          />
        </form>
      </Form>
    </QuoteContext.Provider>
  );
}

function extractDefaultPerilValue(
  defaultConfigPeril: ConfigQuoteChosenWarrantyDto | undefined,
  options: string[],
  required: boolean = false,
): string {
  if (!defaultConfigPeril) {
    return options.length > 0 ? options[0] : String(required);
  }

  if (defaultConfigPeril.option && options.includes(defaultConfigPeril.option)) {
    return defaultConfigPeril.option;
  }

  return options.length > 0 ? options[0] : 'true';
}
